import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  Grid,
  CircularProgress,
  Link,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  Alert
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { tenantSettingsAPI } from '@/services/api';
const DEFAULT_BG_COLOR = '#4a90e2';
const DEFAULT_LOGO_URL = 'https://rallyon.app/logo192.png';
const DEFAULT_HERO_URL = 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1675&q=80';
const DEFAULT_TITLE = 'RallyOn';
const DEFAULT_SUBTITLE = 'Streamline your club operations and enhance member experience';

// Validation schema for login form
const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  rememberMe: Yup.boolean()
});

const Login: React.FC = () => {
  const [loginError, setLoginError] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [title, setTitle] = useState<string>(DEFAULT_TITLE);
  const [subtitle, setSubtitle] = useState<string>(DEFAULT_SUBTITLE);
  const [logoUrl, setLogoUrl] = useState<string>(DEFAULT_LOGO_URL);
  const [heroUrl, setHeroUrl] = useState<string>(DEFAULT_HERO_URL);
  const [bgColor, setBgColor] = useState<string>(DEFAULT_BG_COLOR);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  const [settingsError, setSettingsError] = useState<string>('');
  const lastAuthError = useRef<string>('');
  const { login, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch tenant settings on component mount
  useEffect(() => {
    async function fetchSettings() {
      // Only fetch if in standalone mode with a tenant name
      if (import.meta.env.VITE_DEPLOYMENT_MODE === 'standalone' && import.meta.env.VITE_TENANT_NAME) {
        try {
          const response = await tenantSettingsAPI.getPublicSettings(import.meta.env.VITE_TENANT_NAME);

          if (response?.data) {
            const background_colour = response.data.tenant_background_colour || DEFAULT_BG_COLOR;
            const logo_url = response.data.tenant_logo_img || DEFAULT_LOGO_URL;
            const hero_url = response.data.tenant_hero_img || DEFAULT_HERO_URL;
            const title = response.data.tenant_heading_title || DEFAULT_TITLE;
            const subtitle = response.data.tenant_heading_subtitle || DEFAULT_SUBTITLE;
            setBgColor(background_colour);
            setLogoUrl(logo_url);
            setHeroUrl(hero_url);
            setTitle(title);
            setSubtitle(subtitle);
          }
          setSettingsLoaded(true);
        } catch (error: any) {
          console.error('Error fetching tenant settings:', error);
          // Fall back to defaults on error
          setSettingsError('Unable to load customization settings. Using default theme.');
          setSettingsLoaded(true);
        }
      } else {
        // Not in standalone mode, use defaults
        setSettingsLoaded(true);
      }
    }

    fetchSettings();
  }, []); // Empty dependency array - only run once on mount

  // Handle auth errors separately - only update if error actually changed
  useEffect(() => {
    if (error && error !== lastAuthError.current) {
      lastAuthError.current = error;
      setLoginError(error);
    } else if (!error && lastAuthError.current) {
      lastAuthError.current = '';
      setLoginError(''); // Clear login error when auth error is cleared
    }
  }, [error]);
  // Get the intended destination from location state
  const intendedPath = location.state?.from?.pathname + (location.state?.from?.search || '') || '/dashboard';
  const from = intendedPath === '/login' ||
    intendedPath === 'undefined' // if we go directly to the link, there is no intended path
    ? '/dashboard' : intendedPath;

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      rememberMe: false
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setLoginError('');
      try {
        await login(values.email, values.password, values.rememberMe);
        navigate(from, { replace: true });
      } catch (err: any) {
        let errorMessage = 'Login failed. Please try again.';

        // Try to extract error message from different possible sources
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        } else if (err.response?.status === 401) {
          errorMessage = 'Invalid email or password';
        }

        setLoginError(errorMessage);
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: bgColor ? `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}CC 100%)` : 'linear-gradient(135deg, #868CFF 0%, #4318FF 100%)',
      }}
    >
      <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Grid container spacing={0} alignItems="center" justifyContent="center}}">
          {/* Left side - Banner/Image (hidden on mobile) */}
          {!isMobile && (
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <Box sx={{ p: 4, color: 'white', textAlign: 'center' }}>
                <Typography variant="h3" fontWeight="bold" mb={3}>
                  {title}
                </Typography>
                <Typography variant="h6" mb={4}>
                  {subtitle}
                </Typography>
                <Box
                  component="img"
                  src={heroUrl || DEFAULT_HERO_URL}
                  alt="Hero Image"
                  sx={{
                    width: '90%',
                    maxHeight: '400px',
                    objectFit: 'cover',
                    borderRadius: '20px',
                    boxShadow: '0 20px 27px 0 rgba(0, 0, 0, 0.05)'
                  }}
                />
              </Box>
            </Grid>
          )}

          {/* Right side - Login Form */}
          <Grid size={{ xs: 12, sm: 9, md: 6, lg: 5 }}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 5 },
                borderRadius: '20px',
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 20px 27px 0 rgba(0, 0, 0, 0.05)'
              }}
            >
              <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  Welcome Back
                </Typography>
                <Typography variant="body1" color="text.secondary" mt={1}>
                  Enter your email and password to sign in
                </Typography>
              </Box>
              <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1 }}>
                <Box mb={3}>
                  <Typography variant="subtitle2" fontWeight="medium" mb={1}>
                    Email*
                  </Typography>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    placeholder="mail@example.com"
                    autoComplete='none'
                    autoCapitalize='none'
                    autoFocus
                    value={formik.values.email}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (loginError) {
                        setLoginError('');
                      }
                    }}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }
                    }}
                  />
                </Box>

                <Box mb={4}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight="medium">
                      Password*
                    </Typography>
                    <Link component={RouterLink} to="/forgot-password" variant="subtitle2" color="primary">
                      Forgot Password?
                    </Link>
                  </Box>
                  <TextField
                    fullWidth
                    name="password"
                    placeholder="Min. 6 characters"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    value={formik.values.password}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (loginError) {
                        setLoginError('');
                      }
                    }}
                    error={Boolean(formik.touched.password && formik.errors.password) || Boolean(loginError)}
                    helperText={
                      loginError ||
                      (formik.touched.password && formik.errors.password)
                    }
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: 2,
                        bgcolor: 'background.paper'
                      }
                    }}
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.rememberMe}
                      onChange={formik.handleChange}
                      name="rememberMe"
                      color="primary"
                    />
                  }
                  label="Keep me logged in"
                  sx={{ mb: 3 }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 'bold',
                    boxShadow: '0 4px 7px -1px rgba(0,0,0,0.11), 0 2px 4px -1px rgba(0,0,0,0.07)',
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : 'Sign In'}
                </Button>

                <Typography variant="body2" align="center" mt={3}>
                  <Link href="https://cms.theclubatnorthfield.com/uploads/Member_Onboarding_Instructions_be070b51ed.pdf" target="_blank" rel="noopener noreferrer" color="text.secondary">
                    Detailed Onboarding Instructions
                  </Link>
                </Typography>

              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Login;
